import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import Link from '@mui/material/Link';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { API_URL, BASE_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';

const Alert = MuiAlert;

const MASKED_SECRET = '*******************************';
const KEEP_SECRET_SENTINEL = '***KEEP_EXISTING***';

const DEFAULT_SLACK_WS_BASE = 'https://app.chattitude.ai/slickbots-ws';

function slackWsBaseUrl(resellerBaseUrl) {
  const t = (resellerBaseUrl || '').trim().replace(/\/+$/, '');
  if (!t) {
    return BASE_URL || DEFAULT_SLACK_WS_BASE;
  }
  return `${t}/slickbots-ws`;
}

const SlackCredentialsModal = ({
  open,
  onClose,
  onCredentialsSaved,
  tenantId,
  agentId,
  productName = 'Chattitude',
  resellerBaseUrl,
}) => {
  const slackWsBase = useMemo(() => slackWsBaseUrl(resellerBaseUrl), [resellerBaseUrl]);
  const [credentials, setCredentials] = useState({
    slackSigningSecret: '',
    slackBotToken: ''
  });
  const [hasExistingSecrets, setHasExistingSecrets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [encryptedAgentId, setEncryptedAgentId] = useState(null);
  const [encryptedTenantId, setEncryptedTenantId] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copySlashSuccess, setCopySlashSuccess] = useState(false);

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!tenantId || !agentId) {
        setCredentials({ slackSigningSecret: '', slackBotToken: '' });
        setHasExistingSecrets(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const jwtToken = Cookies.getJwtToken();
        const tenant = Cookies.getTenantId();
        const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

        await axios.get(`${API_URL}/agent-slack-credentials`, {
          params: { tenantId, agentId },
          headers
        });
        setCredentials({
          slackSigningSecret: MASKED_SECRET,
          slackBotToken: MASKED_SECRET
        });
        setHasExistingSecrets(true);
      } catch (err) {
        if (err.response?.status === 404) {
          setCredentials({ slackSigningSecret: '', slackBotToken: '' });
          setHasExistingSecrets(false);
        } else {
          setCredentials({ slackSigningSecret: '', slackBotToken: '' });
          setHasExistingSecrets(false);
          setError('Failed to fetch credentials.');
        }
      } finally {
        setLoading(false);
      }
    };
    if (open && tenantId && agentId) {
      fetchCredentials();
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [open, tenantId, agentId]);

  useEffect(() => {
    const fetchEncryptedIds = async () => {
      if (!open || !agentId) return;
      try {
        const response = await axios.post('/agents/encrypt-ids', null, {
          params: { agentId }
        });
        setEncryptedAgentId(response.data.encryptedAgentId);
        setEncryptedTenantId(response.data.encryptedTenantId);
      } catch (err) {
        console.error('Error fetching encrypted IDs:', err);
        setEncryptedAgentId('');
        setEncryptedTenantId('');
      }
    };
    fetchEncryptedIds();
  }, [open, agentId]);

  const buildSlackQuery = () => {
    if (!encryptedAgentId || !encryptedTenantId) return '';
    return [
      `agentId=${encodeURIComponent(encryptedAgentId)}`,
      `tenantId=${encodeURIComponent(encryptedTenantId)}`
    ].join('&');
  };

  const getEventsSubscriptionUrl = () => {
    const q = buildSlackQuery();
    return q ? `${slackWsBase}/api/chat/slack/events?${q}` : '';
  };

  const getSlashCommandsUrl = () => {
    const q = buildSlackQuery();
    return q ? `${slackWsBase}/api/chat/slack/commands?${q}` : '';
  };

  const handleCopyEventsUrl = () => {
    const url = getEventsSubscriptionUrl();
    if (!url) return;
    navigator.clipboard.writeText(url)
      .then(() => setCopySuccess(true))
      .catch(err => console.error('Failed to copy:', err));
  };

  const handleCopySlashCommandsUrl = () => {
    const url = getSlashCommandsUrl();
    if (!url) return;
    navigator.clipboard.writeText(url)
      .then(() => setCopySlashSuccess(true))
      .catch(err => console.error('Failed to copy:', err));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (hasExistingSecrets && (name === 'slackSigningSecret' || name === 'slackBotToken') && value !== MASKED_SECRET) {
      setHasExistingSecrets(false);
    }
    setCredentials(prev => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!hasExistingSecrets) {
      if (!credentials.slackSigningSecret?.trim()) return 'Signing Secret is required.';
      if (!credentials.slackBotToken?.trim()) return 'Bot User OAuth Token is required.';
    } else {
      if (credentials.slackSigningSecret !== MASKED_SECRET && !credentials.slackSigningSecret?.trim()) {
        return 'Signing Secret is required.';
      }
      if (credentials.slackBotToken !== MASKED_SECRET && !credentials.slackBotToken?.trim()) {
        return 'Bot User OAuth Token is required.';
      }
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, severity: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const jwtToken = Cookies.getJwtToken();
      const tenant = Cookies.getTenantId();
      const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : (tenant ? { Authorization: String(tenant) } : {});

      const payload = {
        tenantId: parseInt(tenantId, 10),
        agentId: parseInt(agentId, 10),
        slackSigningSecret: (hasExistingSecrets && credentials.slackSigningSecret === MASKED_SECRET)
          ? KEEP_SECRET_SENTINEL
          : credentials.slackSigningSecret?.trim() || '',
        slackBotToken: (hasExistingSecrets && credentials.slackBotToken === MASKED_SECRET)
          ? KEEP_SECRET_SENTINEL
          : credentials.slackBotToken?.trim() || ''
      };

      await axios.post(`${API_URL}/agent-slack-credentials`, payload, { headers });
      setSnackbar({ open: true, message: 'Slack credentials saved successfully!', severity: 'success' });
      onCredentialsSaved && onCredentialsSaved();
      setTimeout(() => {
        onClose && onClose();
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save credentials.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const secretPlaceholder = hasExistingSecrets ? 'Leave as-is to keep existing value' : '';

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        sx={{ zIndex: 99999 }}
        PaperProps={{
          sx: {
            zIndex: 99999,
            width: '100%',
            maxWidth: 920,
            maxHeight: '75vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ position: 'relative', pr: 6, flexShrink: 0 }}>
          Slack Credentials
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8, color: '#888', background: '#f5f5f5', zIndex: 21000 }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            overflow: 'visible',
            flex: '0 0 auto',
            px: 3,
            pt: 2,
            pb: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
          ) : (
           
            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 ,marginTop:'15px'}}>
              <TextField
                label="Signing Secret"
                name="slackSigningSecret"
                type="password"
                value={credentials.slackSigningSecret}
                onChange={handleChange}
                required={!hasExistingSecrets}
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder={secretPlaceholder}
                helperText="Slack app → Basic Information → App Credentials → Signing Secret"
              />
              <TextField
                label="Bot User OAuth Token"
                name="slackBotToken"
                type="password"
                value={credentials.slackBotToken}
                onChange={handleChange}
                required={!hasExistingSecrets}
                fullWidth
                InputLabelProps={{ shrink: true }}
                placeholder={secretPlaceholder}
                helperText="Slack app → OAuth & Permissions → Bot User OAuth Token (xoxb-...)"
              />

              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Event Subscriptions Request URL</Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    onClick={handleCopyEventsUrl}
                    disabled={!getEventsSubscriptionUrl()}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                label="Paste this URL in Slack → Event Subscriptions → Request URL"
                value={getEventsSubscriptionUrl()}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                InputProps={{ readOnly: true }}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere',
                  },
                }}
              />

              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Slash Commands Request URL</Typography>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    onClick={handleCopySlashCommandsUrl}
                    disabled={!getSlashCommandsUrl()}
                    size="small"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                label="Paste in Slack → Slash Commands → Request URL (for /start and /close)"
                value={getSlashCommandsUrl()}
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                InputProps={{ readOnly: true }}
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    overflowWrap: 'anywhere',
                  },
                }}
              />

              <Box sx={{ mt: 0.5, p: 2, background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 2, minWidth: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Slack app setup (one-time per workspace)
                </Typography>
                <Box
                  sx={{
                    maxHeight: 200,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    pr: 1,
                    borderTop: '1px solid #e8e8e8',
                    pt: 1,
                  }}
                >
                <List
                  dense
                  disablePadding
                  sx={{
                    color: '#555',
                    marginLeft: '10px',
                    marginRight: '10px',
                    minWidth: 0,
                    '& .MuiListItemText-primary': {
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    },
                    '& a': {
                      wordBreak: 'break-all',
                      overflowWrap: 'anywhere',
                    },
                  }}
                >
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText
                      primary={(
                        <>
                          Create an app by going to{' '}
                          <Link href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer">
                            https://api.slack.com/apps
                          </Link>.
                        </>
                      )}
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary='Click the "Create New App" button and choose "From scratch".' />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary={`Name your App (i.e. ${productName}-Slack), choose the appropriate workspace, and click the "Create App" button.`} />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary={`Under the "Signing Secret" section click the "Show" button and copy and paste that to the "Signing Secret" in ${productName}.`} />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary='Navigate to the OAuth & Permissions tab and scroll down to the "Bot Token Scopes" section. Click the "Add an OAuth Scope" and add the following permissions: app_mentions:read, chat:write, im:write, im:history (and channels:history if using channels).' />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText
                      primary={(
                        <>
                          Install to {'<Your Org>'} and choose the &quot;Allow&quot; button which will take you back to the &quot;OAuth &amp; Permissions&quot; settings.
                        </>
                      )}
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary={`Scroll down to the "OAuth Tokens" section and copy the "Bot User OAuth Token" and paste it in ${productName} under the "Bot User OAuth Token" section.`} />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary={`Click the "Save & Close" button in ${productName} to save your credentials. Once saved open the Slack Credentials again to continue.`} />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary={`Navigate to the "Event Subscriptions" settings and enable events using the slider. From ${productName}, copy the "Event Subscriptions Request URL" and paste it in the "Request URL" section. Once pasted it should display a green "Verified" with a check mark.`} />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary='Click the "Subscribe to bot events" section to expand it then click the "Add Bot User Event" button and add the following three events: app_mention, message.channel, and message.im.' />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary='Navigate to "App Home" from the side menu. Scroll down to the "Your App&apos;s Presence in Slack" section and enable the "Always Show My Bot as Online" option. Check the box for "Allow users to send Slash commands and messages from the messages tab".' />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary={`Navigate to "Slash Commands" and create two commands: /start and /close. Paste the "Slash Commands Request URL" from ${productName} as the Request URL for each command (the URL includes this agent and tenant). Users can also type /start or /close as plain messages in a DM.`} />
                  </ListItem>
                  <ListItem disableGutters sx={{ display: 'list-item', listStyleType: 'decimal', ml: 2, py: 0.25 }}>
                    <ListItemText primary='Click the green "Save Changes" button at the bottom. Once clicked you will see a yellow message at the top stating, "You&apos;ve changed the permission scopes your app uses. Please reinstall your app...". Click the "reinstall your app" link and then click the reinstall button. Once on the screen click the green "Allow" button.' />
                  </ListItem>
                </List>
                </Box>
              </Box>
              {error && (
                <Typography color="error" variant="body2">{error}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, py: 1.5, px: 3, flexShrink: 0, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleSave} variant="outlined" color="primary" disabled={loading} sx={{ minWidth: 180, py: 1 }}>
            SAVE &amp; Close
          </Button>
        </DialogActions>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Snackbar
          open={copySuccess}
          autoHideDuration={2000}
          onClose={() => setCopySuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setCopySuccess(false)} severity="success" variant="filled">
            Event Subscriptions URL copied!
          </Alert>
          </Snackbar>
        <Snackbar
          open={copySlashSuccess}
          autoHideDuration={2000}
          onClose={() => setCopySlashSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setCopySlashSuccess(false)} severity="success" variant="filled">
            Slash Commands URL copied!
          </Alert>
        </Snackbar>
      </Dialog>
    </>
  );
};

export default SlackCredentialsModal;
